import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { NewsItem } from "@/types";

export default function HeroCarousel({ items }: { items: NewsItem[] }) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = window.setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const current = items[index];

  return (
    <section className="relative h-[420px] w-full overflow-hidden bg-primary-950 md:h-[560px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === index ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== index}
        >
          <img
            src={item.cover}
            alt={item.title}
            title={`${item.title} - 亚洲天堂影视资讯`}
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-950/60 to-primary-950/20"></div>

      <div className="relative mx-auto flex h-full w-full max-w-site flex-col justify-end px-4 pb-14 md:px-6 md:pb-20">
        <div className="max-w-2xl animate-fade-up" key={current.id}>
          <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-line" aria-hidden="true"></i>
            焦点推荐
          </span>
          <h2 className="font-heading text-2xl font-bold leading-tight text-background-50 md:text-4xl">
            {current.title}
          </h2>
          <p className="mt-3 line-clamp-2 max-w-xl text-sm leading-relaxed text-primary-100 md:text-base">
            {current.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/news/${current.id}`}
              className="flex items-center gap-1.5 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
            >
              阅读全文
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
            <span className="text-xs text-primary-200">
              {current.source} · {current.date}
            </span>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIndex((i) => (i - 1 + count) % count)}
            aria-label="上一张"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-lg text-background-50 transition-colors hover:bg-background-50/20"
          >
            <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            onClick={() => setIndex((i) => (i + 1) % count)}
            aria-label="下一张"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-background-50/40 text-lg text-background-50 transition-colors hover:bg-background-50/20"
          >
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          </button>
          <div className="ml-1 flex gap-1.5">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到第 ${i + 1} 张`}
                onClick={() => setIndex(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50 hover:bg-background-50/80"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}